/* eslint-disable import/no-anonymous-default-export */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/** @jsx jsx */
import React from 'react'
import { jsx, useThemeUI } from 'theme-ui'
import { cf2imgix } from '../helpers/ev-imgix'
import Card from './trustyle-card'
import { Palette, RichText } from '@uswitch/ev-core/react'
import { useEntity, useMeta } from '@uswitch/ev-utils/context/selectors'
import { getVariantPath } from '@uswitch/ev-utils/get-variant-path'

import { path } from '@uswitch/ev-utils/obj'
import getSizes from '../helpers/ev-imgix/get-sizes'

const DEFAULT_CONTAINER_MAX_WIDTH = 1200

export const cardStylePath = (field, variant) =>
  getVariantPath({
    atomicType: 'compounds',
    componentId: 'card',
    variantName: variant,
    field,
  })

export const BlankCard = () => (
  <div
    sx={{
      display: 'flex',
      width: ({ compounds = {} }) => (compounds.card ? compounds.card.width : 0),
    }}
  />
)

const CardWithConnectedImage = ({ entity, cardProps }) => {
  const { description, date, file } = useEntity(entity)
  const cardMeta = useMeta(['card'])

  const props = {
    critical: false,
    imgSrc: cf2imgix(file?.url),
    imgAlt: description,
    superScript: date,
    ...cardProps,
  }

  if (cardMeta?.clickable && props.linkHref) {
    return (
      <div
        onClick={() => (window.location.href = props.linkHref)}
        sx={{ cursor: 'pointer', variant: cardStylePath('clickWrapper') }}
        role='link'
        tabIndex={0}
      >
        <Card {...props} />
      </div>
    )
  }

  return <Card {...props} />
}

// We are connecting the nested image asset to the card data
const WrappedCard = (props) => {
  const { entity, variant, showDate } = props
  const theme = useThemeUI().theme

  const cardMeta = useMeta(['card'], {})

  const customVariant = path(['variants', variant, 'custom'], cardMeta, false)
  const guide = useEntity(entity)

  const cardTheme = theme.compounds?.card?.variants?.[props.variant] || {}
  const mappedProps = {
    imgSizes: cardTheme?.imgSizes,
  }

  if (props.headerIconSettings) mappedProps.headerChildren = props.headerIconSettings
  if (props.headerChildren)
    mappedProps.headerChildren = RichText({ content: props.headerChildren?.content })
  if (props.contentChildren)
    mappedProps.contentChildren = RichText({ content: props.headerChildren?.content })
  if (cardTheme?.imageSize) mappedProps.imageSize = cardTheme?.imageSize

  const metaImageProps = cardMeta?.variants?.[variant]?.imageProps || {}

  const imageProps = {
    ...metaImageProps,
    imgixParams: {
      auto: 'format,compress',
      ...metaImageProps.imgixParams,
    },
    sizes: getSizes(
      [
        '100vw',
        '100vw',
        path(['layout', 'flex-grid', 'container', 'maxWidth'], theme, DEFAULT_CONTAINER_MAX_WIDTH),
      ],
      theme.breakpoints,
    ),
  }

  const date = new Date(props.customDate || guide.sys?.updatedAt || guide.sys?.createdAt)

  const description = customVariant && guide.sys && guide.sys.trimmedContent

  const linkIconValue = cardMeta?.variants?.[variant]?.linkIcon || null

  return (
    <Choose>
      <When condition={props.image}>
        <CardWithConnectedImage
          entity={props.image}
          cardProps={{
            ...props,
            ...mappedProps,
            linkIcon: linkIconValue,
            imageProps,
            ...(customVariant && guide.sys && { description }),
            ...(showDate && { date }),
          }}
        />
      </When>
      <Otherwise>
        <Card {...props} {...(showDate && { date })} />
      </Otherwise>
    </Choose>
  )
}

export default (props) => (
  <Palette
    px={{
      backgroundColor: 'featureColor',
      p: {
        color: 'textColor',
      },
      'div > h3, div > h3 > a': {
        color: 'accentTextColor',
      },
    }}
    as={WrappedCard}
    {...props}
  />
)
